import React, { useState } from 'react'
import { Button, Form, Spinner } from 'react-bootstrap'
import { ConfigItem } from './ConfigItem'
import AiHelperModal from './AiHelperModal'
import FeatherIcon from 'feather-icons-react'
import httpClient from 'utility/httpClient.util'
import {
  buildReportResourceItem,
  isReportResourceOutput,
  REPORT_RESOURCE_TYPE,
} from './reportResource.util'
import './ResourceMenu.scss'

const resourceMeta = {
  text: {
    icon: 'type',
    accent: '#4099ff',
    description: 'ข้อความทั่วไป',
  },
  image: {
    icon: 'image',
    accent: '#00b894',
    description: 'รูปภาพ / logo',
  },
  pdf: {
    icon: 'file-text',
    accent: '#ef4444',
    description: 'พื้นหลัง PDF',
  },
  textformat: {
    icon: 'hash',
    accent: '#7367f0',
    description: 'เลขหน้า / template',
  },
  date: {
    icon: 'calendar',
    accent: '#f59e0b',
    description: 'วันที่',
  },
  hline: {
    icon: 'minus',
    accent: '#475569',
    description: 'เส้นแนวนอน',
  },
  vline: {
    icon: 'more-vertical',
    accent: '#475569',
    description: 'เส้นแนวตั้ง',
  },
  choice: {
    icon: 'list',
    accent: '#14b8a6',
    description: 'ตัวเลือก',
  },
  check_box: {
    icon: 'check-square',
    accent: '#22c55e',
    description: 'ช่องทำเครื่องหมาย',
  },
  barcode: {
    icon: 'bar-chart-2',
    accent: '#0f172a',
    description: 'Barcode',
  },
  qrcode: {
    icon: 'grid',
    accent: '#334155',
    description: 'QR Code',
  },
  texteditor: {
    icon: 'edit-3',
    accent: '#8b5cf6',
    description: 'Rich text',
  },
  get_api: {
    icon: 'cloud',
    accent: '#0284c7',
    description: 'ดึงค่าจาก API',
  },
  vital_sign: {
    icon: 'activity',
    accent: '#fb7185',
    description: 'กราฟสัญญาณชีพ',
  },
  [REPORT_RESOURCE_TYPE]: {
    icon: 'link',
    accent: '#e83e8c',
    description: 'Linked report resource',
  },
}

const ResourceMenu = ({reportId,onAiApply=()=>{}}) => {
  const [showModal, setShowModal] = useState(false)
  const [resourceSearch, setResourceSearch] = useState('')
  const [resourceItem, setResourceItem] = useState(null)
  const [resourceSearchError, setResourceSearchError] = useState('')
  const [isSearchingResource, setIsSearchingResource] = useState(false)
  //{name:'Vertical line',type:'vline',height:'150px'},
  const menu = [
    { name: 'Text', type: 'text' },
    {
      name: 'Image',
      type: 'image',
      width: '240px',
      height: 'auto',
      opacity: 1,
    },
    {
      name: 'PDF',
      type: 'pdf',
      width: '100%',
      height: '100%',
      fullPage: true,
    },
    { name: 'TextFormat', type: 'textformat',format:'หน้า {page}/{total}' },
    { name: 'Date', type: 'date' },
    { name: 'Horizontal line', type: 'hline', width: '200px' },
    { name: 'Vertical line', type: 'vline', height: '200px' },
    { name: 'Choice', type: 'choice' },
    {
      name: 'Check Box',
      type: 'check_box',
      title: '',
      size: '18px',
      fontSize: '14px',
    },
    { name: 'Barcode', type: 'barcode' },
    { name: 'QRcode', type: 'qrcode' },
    { name: 'Text Editor', type: 'texteditor' },    
    { name: 'Get From API', type: 'get_api', endpoint: '' },
    { name: 'Vital Sign', type: 'vital_sign', componentPath: '/views/panels/hospital/VitalSign/MonitorChart' },
  ]

  const onAiClick = () => {
    setShowModal(true)
  }

  const searchReportResource = async () => {
    const keyword = `${resourceSearch || ''}`.trim()
    if (!keyword) {
      setResourceItem(null)
      setResourceSearchError('กรุณาระบุ Report code หรือ ID')
      return
    }

    setIsSearchingResource(true)
    setResourceItem(null)
    setResourceSearchError('')

    try {
      const isReportTemplateId = /^\d+$/.test(keyword)
      let reportData = null

      if (isReportTemplateId) {
        reportData = await httpClient
          .get(
            `/report/report-webview?reportTemplateId=${keyword}&include=reportTemplate`,
          )
          .then(({ data }) => data.data)
          .catch(() => null)
      }

      if (!reportData?.reportTemplateId) {
        reportData = await httpClient
          .get(
            `/report/report-webview/find?reportCode=${encodeURIComponent(
              keyword,
            )}&include=reportTemplate`,
          )
          .then(({ data }) => data.data)
      }

      if (!reportData?.reportTemplateId) {
        throw new Error('Report not found')
      }
      if (String(reportData.reportTemplateId) === String(reportId)) {
        throw new Error('ไม่สามารถใช้ Report ปัจจุบันเป็น Resource ของตัวเองได้')
      }
      if (!isReportResourceOutput(reportData)) {
        throw new Error('Report นี้ไม่ได้กำหนด Output เป็น Resource')
      }

      const nextResourceItem = buildReportResourceItem(reportData)
      if (!nextResourceItem) {
        throw new Error('Report นี้ไม่มี code จึงใช้เป็น Resource ไม่ได้')
      }

      setResourceItem(nextResourceItem)
    } catch (error) {
      setResourceSearchError(
        error?.message === 'Report not found'
          ? 'Report not found'
          : error?.message || 'ไม่สามารถค้นหา Report ได้',
      )
    } finally {
      setIsSearchingResource(false)
    }
  }

  return (
    <div className="resource-menu">
      <Form
        className="mb-2"
        onSubmit={(event) => {
          event.preventDefault()
          searchReportResource()
        }}
      >
        <Form.Label>Report Resource</Form.Label>
        <div className="d-flex">
          <Form.Control
            type="text"
            value={resourceSearch}
            placeholder="Report code หรือ ID"
            autoComplete="off"
            onChange={(event) => {
              setResourceSearch(event.target.value)
              setResourceItem(null)
              setResourceSearchError('')
            }}
          />
          <Button
            type="submit"
            variant="outline-primary"
            className="ml-1"
            disabled={isSearchingResource}
            title="ค้นหา Report Resource ด้วย code หรือ ID"
          >
            {isSearchingResource ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <FeatherIcon size="16" icon="search" />
            )}
          </Button>
        </div>
        {resourceSearchError && (
          <small className="d-block text-danger mt-1">
            {resourceSearchError}
          </small>
        )}
      </Form>
      {resourceItem && (
        <div className="mb-2">
          <ConfigItem
            item={resourceItem}
            icon={resourceMeta[REPORT_RESOURCE_TYPE].icon}
            accent={resourceMeta[REPORT_RESOURCE_TYPE].accent}
            description={`ลากเพื่อเชื่อม Report code: ${resourceItem.resourceCode}`}
            className="resource-menu-item"
          />
        </div>
      )}
      <div className="resource-menu-list">
        {menu.map((item, index) => {
          const meta = resourceMeta[item.type] || {}
          return (
            <ConfigItem
              key={index}
              item={item}
              icon={meta.icon}
              accent={meta.accent}
              description={meta.description}
              className="resource-menu-item"
            />
          )
        })}
      </div>
      <Button
        color="primary"
        outline
        className="resource-ai-button"
        onClick={onAiClick}
      >
        <span className="resource-ai-icon">
          <FeatherIcon size="18" icon="cpu" />
        </span>
        <span>AI Helper</span>
      </Button>
      <AiHelperModal show={showModal} onHide={() => setShowModal(false)} data={{reportId:reportId}} onApply={(obj)=>{onAiApply(obj);setShowModal(false);}}/>
    </div>
  )
}

export default ResourceMenu
